import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiSearch } from '@assets/js/api/commonApi.js';
import '@assets/css/main-content.css';

/**
 * 토너먼트 시스템 메인 화면
 */
const MainContent = () => {
    const navigate = useNavigate();
    const [availableCompetitions, setAvailableCompetitions] = useState([]);
    const [ongoingCompetitions, setOngoingCompetitions] = useState([]);
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const [bracketList, setBracketList] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState([]); // 'waiting', 'ongoing', 'finished'
    const [selectedDivisions, setSelectedDivisions] = useState([]);
    const [selectedWeightClasses, setSelectedWeightClasses] = useState([]);
    const [selectedArmTypes, setSelectedArmTypes] = useState([]); // 'L', 'R'
    const [noticeModalOpen, setNoticeModalOpen] = useState(false);

    // 오늘 날짜 (YYYY-MM-DD 형식)
    const getTodayString = () => {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    // 신청 가능 여부 확인
    const isApplicationAvailable = useCallback((competition) => {
        if (!competition.formStrtDate || !competition.formDeadDate) {
            return false;
        }
        const todayString = getTodayString();
        return todayString >= competition.formStrtDate && todayString <= competition.formDeadDate;
    }, []);

    // 진행중 여부 확인
    const isOngoing = useCallback((competition) => {
        if (!competition.strtDate || !competition.endDate) {
            return false;
        }
        const todayString = getTodayString();
        return todayString >= competition.strtDate && todayString <= competition.endDate;
    }, []);

    // 대회 목록 조회
    const loadCompetitions = useCallback(() => {
        ApiSearch('/ve/competition/search/list', { useYn: 'Y' }).then(res => {
            if (res && res.success && res.data && res.data.dataList) {
                const competitions = res.data.dataList;

                // 신청 가능한 대회 필터링 (추후 옵션으로 신청 불가 처리 필요시 적용)
                const available = competitions.filter(comp => isApplicationAvailable(comp));
                setAvailableCompetitions(available);

                // 진행중인 대회 필터링
                const ongoing = competitions.filter(comp => isOngoing(comp));
                setOngoingCompetitions(ongoing);
            }
        });

    }, [isApplicationAvailable, isOngoing]);

    // 대회 신청 페이지로 이동
    const goToCompetition = () => {
        navigate('/ve/competition');
    };

    // 대진별 정렬 함수 (부문별 낮은 체급 우선)
    const sortBrackets = (brackets) => {
        return [...brackets].sort((a, b) => {
            // 부문명으로 먼저 정렬
            if (a.dvsnNm < b.dvsnNm) return -1;
            if (a.dvsnNm > b.dvsnNm) return 1;

            // 부문이 같으면 체급으로 정렬 (낮은 체급 우선)
            // 체급명에서 숫자 추출하여 비교
            const getWeight = (wetclsNm) => {
                if (!wetclsNm) return 0;
                const match = wetclsNm.match(/\d+/);
                return match ? parseInt(match[0]) : 0;
            };

            return getWeight(a.wetclsNm) - getWeight(b.wetclsNm);
        });
    };

    // 대회 선택 핸들러 (대진 부문별 진행상태 조회)
    const handleCompetitionSelect = useCallback((competition) => {
        // 이미 선택된 대회를 다시 클릭하면 토글 (접기)
        if (selectedCompetition && selectedCompetition.comptnCd === competition.comptnCd) {
            setSelectedCompetition(null);
            setBracketList([]);
            setSelectedStatuses([]);
            setSelectedDivisions([]);
            setSelectedWeightClasses([]);
            setSelectedArmTypes([]);
            return;
        }

        // 새로운 대회 선택 (펼치기)
        setSelectedStatuses([]);
        setSelectedDivisions([]);
        setSelectedWeightClasses([]);
        setSelectedArmTypes([]);
        ApiSearch('/ve/competition/mtmkgstat/search/list', competition).then(res => {
            if (res && res.success && res.data && res.data.dataList) {
                const sortedBrackets = sortBrackets(res.data.dataList);
                setBracketList(sortedBrackets);
                setSelectedCompetition(competition);
            } else {
                setBracketList([]);
                setSelectedCompetition(null);
            }
        });
    }, [selectedCompetition]);

    // 알림 팝업 초기화 - localStorage 체크
    useEffect(() => {
        const noticeClosedDate = localStorage.getItem('noticeClosedDate');
        const todayString = getTodayString();

        // 저장된 날짜가 없거나 오늘이 아니면 팝업 표시
        if (!noticeClosedDate || noticeClosedDate !== todayString) {
            setNoticeModalOpen(true);
        }
    }, []);

    // 알림 팝업 닫기
    const handleNoticeModalClose = () => {
        setNoticeModalOpen(false);
    };

    // 오늘 그만보기
    const handleNoticeModalCloseToday = () => {
        const todayString = getTodayString();
        localStorage.setItem('noticeClosedDate', todayString);
        setNoticeModalOpen(false);
    };

    useEffect(() => {
        loadCompetitions();
    }, [loadCompetitions]);

    // 진행중인 대회가 로드되면 첫번째 대회 자동 선택 (초기 로딩 시에만)
    useEffect(() => {
        if (ongoingCompetitions.length > 0 && !selectedCompetition && isInitialLoad) {
            handleCompetitionSelect(ongoingCompetitions[0]);
            setIsInitialLoad(false);
        }
    }, [ongoingCompetitions, selectedCompetition, handleCompetitionSelect, isInitialLoad]);

    // 진행상태에 따른 상태 색상 클래스 반환
    const getStatusColorClass = (status) => {
        if (!status) return 'status-waiting';

        const statusLower = status.toLowerCase();
        if (statusLower.includes('대기') || statusLower.includes('wait')) {
            return 'status-waiting';
        } else if (statusLower.includes('진행') || statusLower.includes('progress') || statusLower.includes('ongoing')) {
            return 'status-ongoing';
        } else if (statusLower.includes('종료') || statusLower.includes('end') || statusLower.includes('finish')) {
            return 'status-finished';
        }
        return 'status-waiting';
    };

    // 진행 텍스트 표시.
    const getStatusNm = (statusCd) => {
        if (statusCd === 'C' || statusCd === 'R') {
            //확정or대기
            return '대기중';
        } else if (statusCd === 'E') {
            return '종료';
        } else {
            return '진행중';
        }
    }

    // 팔 타입을 한글로 변환
    const getArmTypeNm = (armType) => {
        if (armType === 'L') {
            return '왼팔';
        } else if (armType === 'R') {
            return '오른팔';
        } else {
            return '-';
        }
    };

    // 상태 코드를 필터 값으로 변환
    const getFilterValue = (statusCd) => {
        if (statusCd === 'C' || statusCd === 'R') {
            return 'waiting';
        } else if (statusCd === 'E') {
            return 'finished';
        } else {
            return 'ongoing';
        }
    }

    // 부문/체급 목록 추출
    const getUniqueValues = useCallback((key) => {
        const values = bracketList.map(bracket => bracket[key]).filter(Boolean);
        return [...new Set(values)].sort();
    }, [bracketList]);

    const divisions = getUniqueValues('dvsnNm');
    const weightClasses = getUniqueValues('wetclsNm');

    // 필터 모달 열기
    const handleFilterModalOpen = () => {
        setFilterModalOpen(true);
    };

    // 필터 모달 닫기
    const handleFilterModalClose = () => {
        setFilterModalOpen(false);
    };

    // 필터 적용
    const handleFilterApply = () => {
        setFilterModalOpen(false);
    };

    // 필터 초기화
    const handleFilterReset = () => {
        setSelectedStatuses([]);
        setSelectedDivisions([]);
        setSelectedWeightClasses([]);
        setSelectedArmTypes([]);
    };

    // 진행상황 체크박스 토글
    const toggleStatus = (status) => {
        setSelectedStatuses(prev =>
            prev.includes(status)
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    // 부문 체크박스 토글
    const toggleDivision = (division) => {
        setSelectedDivisions(prev =>
            prev.includes(division)
                ? prev.filter(d => d !== division)
                : [...prev, division]
        );
    };

    // 체급 체크박스 토글
    const toggleWeightClass = (weightClass) => {
        setSelectedWeightClasses(prev =>
            prev.includes(weightClass)
                ? prev.filter(w => w !== weightClass)
                : [...prev, weightClass]
        );
    };

    // 팔 유형 체크박스 토글
    const toggleArmType = (armType) => {
        setSelectedArmTypes(prev =>
            prev.includes(armType)
                ? prev.filter(a => a !== armType)
                : [...prev, armType]
        );
    };

    // 필터링된 대진 목록
    const filteredBracketList = bracketList.filter(bracket => {
        // 진행상황 필터
        if (selectedStatuses.length > 0 && !selectedStatuses.includes(getFilterValue(bracket.mtmkgStat))) {
            return false;
        }

        // 부문 필터
        if (selectedDivisions.length > 0 && !selectedDivisions.includes(bracket.dvsnNm)) {
            return false;
        }

        // 체급 필터
        if (selectedWeightClasses.length > 0 && !selectedWeightClasses.includes(bracket.wetclsNm)) {
            return false;
        }

        // 팔 유형 필터
        if (selectedArmTypes.length > 0 && !selectedArmTypes.includes(bracket.armType)) {
            return false;
        }

        return true;
    });

    // 대진 카드 클릭 - 토너먼트 상세 페이지로 이동
    const handleBracketClick = (bracket) => {
        navigate(`/ve/trmnt-maker?mngCd=${bracket.mngCd}&mtmkgCd=${bracket.mtmkgCd}&armType=${bracket.armType}&isFromHome=true`);
    };

    // 진행중인지 확인
    // const isStatusOngoing = (status) => {
    //     if (!status) return false;
    //     const statusLower = status.toLowerCase();
    //     return statusLower.includes('진행') || statusLower.includes('progress') || statusLower.includes('ongoing');
    // };

    return (
        <div className="main-content-wrapper">
            {/* 상단 알림 영역 */}
            <div className="announcement-section">
                <div className="container-fluid py-4">
                    <div className="announcement-card">
                        <div className="announcement-icon">
                            <i className="bi bi-megaphone-fill"></i>
                        </div>
                        <div className="announcement-content">
                            <h5 className="announcement-title">Sort Tournament Maker 안내</h5>
                            <ul className="announcement-list">
                                <li>
                                    <i className="bi bi-diagram-3"></i>
                                    <span><strong>토너먼트 메뉴</strong>를 활용해서 심플한 대진표를 만들어 보세요.</span>
                                </li>
                                <li>
                                    <i className="bi bi-clipboard-check"></i>
                                    <span><strong>신청 가능 대회</strong>를 확인하고 대회에 참여해보세요.</span>
                                </li>
                                <li>
                                    <i className="bi bi-play-circle"></i>
                                    <span><strong>진행중인 대회</strong>를 실시간으로 확인할 수 있습니다.</span>
                                </li>
                                <li>
                                    <i className="bi bi-gear"></i>
                                    <span><strong>토너먼트 관리 시스템</strong>에서 실용적으로 대회를 관리해 보세요.</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* 메인 영역-1: 대회 정보 */}
            <div className="available-competitions-section">
                <div className="container-fluid py-4">
                    <div className="section-header">
                        <div className="section-header-top">
                            <h4 className="section-title">
                                <i className="bi bi-calendar-check me-2"></i>
                                대회 정보
                            </h4>
                            <div className="competition-legend">
                                <div className="legend-item">
                                    <span className="legend-indicator available"></span>
                                    <span className="legend-text">신청가능</span>
                                </div>
                                <div className="legend-item">
                                    <span className="legend-indicator unavailable"></span>
                                    <span className="legend-text">신청불가</span>
                                </div>
                            </div>
                        </div>
                        <p className="section-subtitle">지금 신청할 수 있는 대회를 확인하고 참여해보세요.</p>
                    </div>

                    {availableCompetitions.length === 0 ? (
                        <div className="empty-state">
                            <i className="bi bi-inbox"></i>
                            <p>현재 신청 가능한 대회가 없습니다</p>
                        </div>
                    ) : (
                        <div className="competitions-grid">
                            {availableCompetitions.map((competition, index) => (
                                <div key={index} className="competition-card available-card">
                                    <div className="card-corner-indicator available"></div>
                                    <div className="card-body">
                                        <h5 className="competition-name">{competition.comptnNm}</h5>
                                        <div className="competition-info">
                                            <div className="info-item">
                                                <i className="bi bi-building me-2"></i>
                                                <span className="info-label">주최:</span>
                                                <span className="info-value">{competition.hostOrgNm || '-'}</span>
                                            </div>
                                            <div className="info-item">
                                                <i className="bi bi-calendar3 me-2"></i>
                                                <span className="info-label">신청기간:</span>
                                                <span className="info-value">
                                                    {competition.formStrtDate === competition.formDeadDate
                                                        ? competition.formStrtDate
                                                        : `${competition.formStrtDate} ~ ${competition.formDeadDate}`}
                                                </span>
                                            </div>
                                            <div className="info-item">
                                                <i className="bi bi-calendar-check me-2"></i>
                                                <span className="info-label">대회일자:</span>
                                                <span className="info-value">
                                                    {competition.strtDate === competition.endDate
                                                        ? competition.strtDate
                                                        : `${competition.strtDate} ~ ${competition.endDate}`}
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            className="btn btn-primary btn-apply w-100 mt-3"
                                            onClick={goToCompetition}
                                        >
                                            <i className="bi bi-pencil-square me-2"></i>
                                            대회 신청 하러 하기
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 메인 영역-2: 진행중인 대회 */}
            <div className="ongoing-competitions-section">
                <div className="container-fluid py-4">
                    <div className="section-header">
                        <h4 className="section-title mb-2">
                            <i className="bi bi-play-circle me-2"></i>
                            진행중인 대회
                        </h4>
                        <p className="section-subtitle">현재 진행중인 대회를 선택하여 진행 상황을 확인할 수 있습니다.</p>
                    </div>

                    {ongoingCompetitions.length === 0 ? (
                        <div className="empty-state">
                            <i className="bi bi-inbox"></i>
                            <p>현재 진행중인 대회가 없습니다</p>
                        </div>
                    ) : (
                        <>
                            <div className="ongoing-grid">
                                {ongoingCompetitions.map((competition, index) => (
                                    <div
                                        key={index}
                                        className={`ongoing-card ${selectedCompetition?.comptnCd === competition.comptnCd ? 'selected' : ''}`}
                                        onClick={() => handleCompetitionSelect(competition)}
                                    >
                                        <div className="ongoing-card-header">
                                            <h5 className="ongoing-competition-name">{competition.comptnNm}</h5>
                                            <span className="live-badge">LIVE</span>
                                        </div>
                                        <div className="ongoing-card-body">
                                            <div className="ongoing-info">
                                                <div className="ongoing-info-item">
                                                    <i className="bi bi-building me-1"></i>&nbsp;
                                                    <span className="info-label">주최:&nbsp;</span>
                                                    <span className="info-value">{competition.hostOrgNm || '-'}</span>
                                                </div>
                                                <div className="ongoing-info-item">
                                                    <i className="bi bi-tag-fill me-1"></i>&nbsp;
                                                    <span className="info-label">부문:&nbsp;</span>
                                                    <span className="info-value">{competition.dvsnNm || '-'}</span>
                                                </div>
                                                <div className="ongoing-info-item">
                                                    <i className="bi bi-speedometer2 me-1"></i>&nbsp;
                                                    <span className="info-label">체급:&nbsp;</span>
                                                    <span className="info-value">{competition.wetclsNm || '-'}</span>
                                                </div>
                                                <div className="ongoing-info-item">
                                                    <i className="bi bi-calendar-event me-1"></i>&nbsp;
                                                    <span className="info-label">대회일자:&nbsp;</span>
                                                    <span className="info-value">
                                                        {competition.strtDate === competition.endDate
                                                            ? competition.strtDate
                                                            : `${competition.strtDate} ~ ${competition.endDate}`}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="ongoing-card-footer">
                                            {selectedCompetition?.comptnCd === competition.comptnCd ? (
                                                <i className="bi bi-chevron-up"></i>
                                            ) : (
                                                <i className="bi bi-chevron-down"></i>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* 선택된 대회의 대진별 진행상태 */}
                            {selectedCompetition && bracketList.length > 0 && (
                                <div className="bracket-status-section">
                                    <div className="bracket-status-header">
                                        <h5>
                                            <i className="bi bi-diagram-3 me-2"></i>&nbsp;
                                            {selectedCompetition.comptnNm}
                                        </h5>
                                        <div className="bracket-status-filter">
                                            <button
                                                className="filter-icon-btn"
                                                onClick={handleFilterModalOpen}
                                                title="대진별 필터"
                                            >
                                                <i className="bi bi-funnel-fill"></i>
                                            </button>

                                            {/* 선택된 필터 표시 */}
                                            {(selectedStatuses.length > 0 || selectedDivisions.length > 0 || selectedWeightClasses.length > 0 || selectedArmTypes.length > 0) && (
                                                <div className="selected-filters">
                                                    {selectedStatuses.map((status, idx) => {
                                                        const statusLabel = status === 'waiting' ? '대기중' : status === 'ongoing' ? '진행중' : '종료';
                                                        return (
                                                            <span key={`status-${idx}`} className="filter-tag">
                                                                {statusLabel}
                                                                <button
                                                                    className="filter-tag-remove"
                                                                    onClick={() => toggleStatus(status)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        );
                                                    })}
                                                    {selectedDivisions.map((division, idx) => (
                                                        <span key={`div-${idx}`} className="filter-tag">
                                                            {division}
                                                            <button
                                                                className="filter-tag-remove"
                                                                onClick={() => toggleDivision(division)}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                    {selectedWeightClasses.map((weightClass, idx) => (
                                                        <span key={`wc-${idx}`} className="filter-tag">
                                                            {weightClass}
                                                            <button
                                                                className="filter-tag-remove"
                                                                onClick={() => toggleWeightClass(weightClass)}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                    {selectedArmTypes.map((armType, idx) => {
                                                        const armTypeLabel = armType === 'L' ? '왼팔' : '오른팔';
                                                        return (
                                                            <span key={`arm-${idx}`} className="filter-tag">
                                                                {armTypeLabel}
                                                                <button
                                                                    className="filter-tag-remove"
                                                                    onClick={() => toggleArmType(armType)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="bracket-status-grid">
                                        {filteredBracketList.map((bracket, index) => (
                                            <div
                                                key={index}
                                                className="bracket-item"
                                                onClick={() => handleBracketClick(bracket)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="bracket-item-header">
                                                    <span className="bracket-title">
                                                        {bracket.mtmkgNm || ''}
                                                    </span>
                                                    <span className={`bracket-status-indicator ${getStatusColorClass(bracket.mtmkgStatNm)}`}>
                                                        <i className="bi bi-circle-fill"></i>
                                                        <span className="status-text">{getStatusNm(bracket.mtmkgStat)}</span>
                                                    </span>
                                                </div>
                                                <div className="bracket-item-body">
                                                    <div className="bracket-detail">
                                                        <i className="bi bi-tag-fill me-2"></i>
                                                        <span className="detail-label">부문:</span>
                                                        <span className="detail-value">{bracket.dvsnNm || '-'}</span>
                                                    </div>
                                                    <div className="bracket-detail">
                                                        <i className="bi bi-speedometer2 me-2"></i>
                                                        <span className="detail-label">체급:</span>
                                                        <span className="detail-value">{bracket.wetclsNm || '-'}</span>
                                                    </div>
                                                    <div className="bracket-detail">
                                                        <i className="bi bi-speedometer2 me-2"></i>
                                                        <span className="detail-label">타입:</span>
                                                        <span className="detail-value">{getArmTypeNm(bracket.armType)}</span>
                                                    </div>
                                                    <div className="bracket-detail">
                                                        <i className="bi bi-speedometer2 me-2"></i>
                                                        <span className="detail-label">경기수:</span>
                                                        <span className="detail-value">{bracket.trmntCnt || '-'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* 필터 모달 */}
            {filterModalOpen && (
                <div className="filter-modal-overlay" onClick={handleFilterModalClose}>
                    <div className="filter-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="filter-modal-header">
                            <h5>
                                <i className="bi bi-funnel-fill me-2"></i>
                                대진별 필터
                            </h5>
                            <button className="filter-modal-close" onClick={handleFilterModalClose}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        <div className="filter-modal-body">
                            {/* 진행상황 필터 */}
                            <div className="filter-group">
                                <h6 className="filter-group-title">
                                    <i className="bi bi-hourglass-split me-2"></i>
                                    진행상황
                                </h6>
                                <div className="filter-checkbox-grid">
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedStatuses.includes('waiting')}
                                            onChange={() => toggleStatus('waiting')}
                                        />
                                        <span>대기중</span>
                                    </label>
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedStatuses.includes('ongoing')}
                                            onChange={() => toggleStatus('ongoing')}
                                        />
                                        <span>진행중</span>
                                    </label>
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedStatuses.includes('finished')}
                                            onChange={() => toggleStatus('finished')}
                                        />
                                        <span>종료</span>
                                    </label>
                                </div>
                            </div>

                            {/* 부문 필터 */}
                            {divisions.length > 0 && (
                                <div className="filter-group">
                                    <h6 className="filter-group-title">
                                        <i className="bi bi-tag-fill me-2"></i>
                                        부문
                                    </h6>
                                    <div className="filter-checkbox-grid">
                                        {divisions.map((division, idx) => (
                                            <label key={idx} className="filter-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDivisions.includes(division)}
                                                    onChange={() => toggleDivision(division)}
                                                />
                                                <span>{division}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 체급 필터 */}
                            {weightClasses.length > 0 && (
                                <div className="filter-group">
                                    <h6 className="filter-group-title">
                                        <i className="bi bi-speedometer2 me-2"></i>
                                        체급
                                    </h6>
                                    <div className="filter-checkbox-grid">
                                        {weightClasses.map((weightClass, idx) => (
                                            <label key={idx} className="filter-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedWeightClasses.includes(weightClass)}
                                                    onChange={() => toggleWeightClass(weightClass)}
                                                />
                                                <span>{weightClass}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 팔 유형 필터 */}
                            <div className="filter-group">
                                <h6 className="filter-group-title">
                                    <i className="bi bi-hand-thumbs-up me-2"></i>
                                    팔 유형
                                </h6>
                                <div className="filter-checkbox-grid">
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedArmTypes.includes('L')}
                                            onChange={() => toggleArmType('L')}
                                        />
                                        <span>왼팔</span>
                                    </label>
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedArmTypes.includes('R')}
                                            onChange={() => toggleArmType('R')}
                                        />
                                        <span>오른팔</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="filter-modal-footer">
                            <button className="btn btn-secondary" onClick={handleFilterReset}>
                                초기화
                            </button>
                            <div className="filter-modal-footer-right">
                                <button className="btn btn-outline-secondary" onClick={handleFilterModalClose}>
                                    닫기
                                </button>
                                <button className="btn btn-primary" onClick={handleFilterApply}>
                                    확인
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 알림 팝업 모달 */}
            {noticeModalOpen && (
                <div
                    className="notice-modal-overlay"
                    onClick={handleNoticeModalClose}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                >
                    <div
                        className="notice-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '500px',
                            width: '90%',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            position: 'relative'
                        }}
                    >
                        {/* 닫기 버튼 */}
                        <button
                            onClick={handleNoticeModalClose}
                            style={{
                                position: 'absolute',
                                top: '15px',
                                right: '15px',
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                cursor: 'pointer',
                                color: '#6c757d',
                                padding: 0,
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            ×
                        </button>

                        {/* 알림 내용 */}
                        <div style={{ marginTop: '10px' }}>
                            <h4 style={{
                                color: '#333',
                                marginBottom: '20px',
                                fontSize: '22px',
                                fontWeight: 'bold'
                            }}>
                                <i className="bi bi-bell-fill me-2" style={{ color: '#0d6efd' }}></i>
                                알림
                            </h4>
                            <div style={{
                                color: '#555',
                                lineHeight: '1.6',
                                fontSize: '15px',
                                marginBottom: '25px'
                            }}>
                                <p className='mb-2' style={{ marginBottom: '15px' }}>
                                    <strong>Tournament Maker</strong>에 오신 것을 환영합니다!
                                </p>
                                <ul style={{
                                    listStyle: 'none',
                                    padding: 0,
                                    margin: 0
                                }}>
                                    <li style={{ marginBottom: '10px' }}>
                                        <i className="bi bi-check-circle-fill me-2" style={{ color: '#198754' }}></i>
                                        테스트 기간입니다. 테스트 데이터가 보일 수 있습니다.
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        <i className="bi bi-check-circle-fill me-2" style={{ color: '#198754' }}></i>
                                        메뉴는 정상적으로 사용할 수 있습니다.
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        <i className="bi bi-check-circle-fill me-2" style={{ color: '#198754' }}></i>
                                        문제가 생기면 hatssarjy@gmail.com으로 연락주세요.
                                    </li>
                                </ul>
                            </div>

                            {/* 버튼 그룹 */}
                            <div style={{
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'flex-end'
                            }}>
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleNoticeModalCloseToday}
                                    style={{
                                        padding: '8px 20px',
                                        fontSize: '14px'
                                    }}
                                >
                                    오늘 그만보기
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleNoticeModalClose}
                                    style={{
                                        padding: '8px 20px',
                                        fontSize: '14px'
                                    }}
                                >
                                    확인
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MainContent;
